import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Database, Search, Download, Upload, FileText, FileSpreadsheet, Phone, Mail, Tag, X, LayoutGrid, TableProperties, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PREDEFINED_TAGS = ['Hot Lead', 'Do Not Contact', 'Follow Up', 'Blacklisted', 'On Hold'];
const TAG_COLORS = {
  'Hot Lead': 'bg-red-100 text-red-800 border-red-200',
  'Do Not Contact': 'bg-slate-800 text-white border-slate-900',
  'Follow Up': 'bg-amber-100 text-amber-800 border-amber-200',
  'Blacklisted': 'bg-red-900 text-white border-red-900',
  'On Hold': 'bg-blue-100 text-blue-800 border-blue-200',
};
const DEFAULT_TAG_COLOR = 'bg-purple-100 text-purple-800 border-purple-200';

const PIPELINE_STAGES = [
  { key: 'Sourced', color: 'bg-slate-500', light: 'bg-slate-50 border-slate-200', text: 'text-slate-700' },
  { key: 'Screened', color: 'bg-blue-500', light: 'bg-blue-50 border-blue-200', text: 'text-blue-700' },
  { key: 'Shortlisted', color: 'bg-cyan-500', light: 'bg-cyan-50 border-cyan-200', text: 'text-cyan-700' },
  { key: 'Interview', color: 'bg-amber-500', light: 'bg-amber-50 border-amber-200', text: 'text-amber-700' },
  { key: 'Offered', color: 'bg-emerald-500', light: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700' },
  { key: 'Joined', color: 'bg-green-600', light: 'bg-green-50 border-green-200', text: 'text-green-700' },
];

export default function TalentVault() {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('kanban');
  const [searchText, setSearchText] = useState('');
  const [filterSource, setFilterSource] = useState('all');
  const [filterClient, setFilterClient] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTag, setFilterTag] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [tagOpen, setTagOpen] = useState(false);
  const [tagCandidate, setTagCandidate] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [notes, setNotes] = useState('');
  const [customTag, setCustomTag] = useState('');
  const [saving, setSaving] = useState(false);
  const [aiSearchOpen, setAiSearchOpen] = useState(false);
  const [aiQuery, setAiQuery] = useState('');
  const [aiResults, setAiResults] = useState(null);
  const [aiSearching, setAiSearching] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
  const canExport = user.role === 'admin' || user.role === 'team_leader';

  const fetchCandidates = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/talent-vault`, { headers: getHeaders() });
      setCandidates(res.data);
    } catch { toast.error('Failed to fetch candidates'); }
    finally { setLoading(false); }
  };

  // Standard fetch-on-mount pattern. setState inside the effect is intentional
  // (we hydrate the candidates list).
  useEffect(() => { fetchCandidates(); }, []);

  const clients = useMemo(() => [...new Set(candidates.map(c => c.client).filter(Boolean))].sort(), [candidates]);
  const statuses = useMemo(() => [...new Set(candidates.map(c => c.status).filter(Boolean))].sort(), [candidates]);
  const allTags = useMemo(() => {
    const tags = new Set();
    candidates.forEach(c => (c.tags || []).forEach(t => tags.add(t)));
    return [...tags].sort();
  }, [candidates]);

  const filtered = useMemo(() => {
    return candidates.filter(c => {
      if (searchText) {
        const q = searchText.toLowerCase();
        if (!(
          (c.candidate_name || '').toLowerCase().includes(q) ||
          (c.phone || '').includes(q) ||
          (c.email || '').toLowerCase().includes(q) ||
          (c.position || '').toLowerCase().includes(q) ||
          (c.client || '').toLowerCase().includes(q)
        )) return false;
      }
      if (filterSource !== 'all') {
        if (filterSource === 'resume' && !c.source?.includes('Resume')) return false;
        if (filterSource === 'ccr' && !c.source?.includes('CCR')) return false;
        if (filterSource === 'both' && !(c.source?.includes('Resume') && c.source?.includes('CCR'))) return false;
      }
      if (filterClient !== 'all' && c.client !== filterClient) return false;
      if (filterStatus !== 'all' && c.status !== filterStatus) return false;
      if (filterTag !== 'all' && !(c.tags || []).includes(filterTag)) return false;
      if (filterDateFrom && c.date < filterDateFrom) return false;
      if (filterDateTo && c.date > filterDateTo) return false;
      return true;
    });
  }, [candidates, searchText, filterSource, filterClient, filterStatus, filterTag, filterDateFrom, filterDateTo]);

  // Pagination state for the Table view. Backend already returns rows sorted
  // by date desc (latest first), so page 1 always shows the newest candidates.
  // Kanban view continues to use the full filtered list so per-stage counts
  // stay accurate — pagination is only relevant for the flat table.
  const PAGE_SIZE = 50;
  const [pageState, setPageState] = useState({ page: 1, signature: '' });
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  // A stable signature of the current filter set. When it changes we reset to
  // page 1 inline — no useEffect needed (avoids the set-state-in-effect lint
  // rule and a wasted render).
  const filterSignature = `${searchText}|${filterSource}|${filterClient}|${filterStatus}|${filterTag}|${filterDateFrom}|${filterDateTo}|${viewMode}`;
  const requestedPage = pageState.signature === filterSignature ? pageState.page : 1;
  const effectivePage = Math.min(Math.max(1, requestedPage), totalPages);
  const setCurrentPage = (next) => {
    const nextPage = typeof next === 'function' ? next(effectivePage) : next;
    setPageState({ page: nextPage, signature: filterSignature });
  };
  const pagedRows = useMemo(
    () => filtered.slice((effectivePage - 1) * PAGE_SIZE, effectivePage * PAGE_SIZE),
    [filtered, effectivePage]
  );

  const handleExport = async (format) => {
    try {
      const res = await axios.get(`${API}/talent-vault/export?format=${format}`, { headers: getHeaders(), responseType: 'blob' });
      // If the server returned an error JSON body (5xx/4xx) with our `responseType: 'blob'`,
      // we need to peel it out of the Blob before we can show a useful message.
      if (res.data && res.data.type && res.data.type.includes('application/json')) {
        const text = await res.data.text();
        let parsed;
        try { parsed = JSON.parse(text); } catch { parsed = { detail: text }; }
        toast.error(parsed?.detail || 'Export failed');
        return;
      }
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `talent_vault.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      a.click(); window.URL.revokeObjectURL(url);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (err) {
      // Surface server error body even when response type is blob.
      let serverMsg = '';
      try {
        if (err?.response?.data instanceof Blob) {
          serverMsg = await err.response.data.text();
        } else if (typeof err?.response?.data === 'string') {
          serverMsg = err.response.data;
        }
      } catch (err) { console.error("./pages/TalentVault.jsx:", err); }
      console.error('TalentVault export failed:', err?.response?.status, serverMsg || err?.message);
      const status = err?.response?.status;
      toast.error(status === 403 ? 'You don\'t have permission to export' : `Export failed${status ? ` (${status})` : ''}`);
    }
  };

  const [importErrors, setImportErrors] = useState([]);
  const handleImport = async (e) => {
    e.preventDefault();
    if (!importFile) { toast.error('Select a CSV file'); return; }
    setImporting(true);
    setImportErrors([]);
    try {
      const formData = new FormData(); formData.append('file', importFile);
      const res = await axios.post(`${API}/talent-vault/import`, formData, { headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' } });
      const { imported, skipped, errors = [] } = res.data;
      if (imported > 0 && skipped === 0) {
        toast.success(`✅ All ${imported} candidates imported successfully`);
        setImportOpen(false); setImportFile(null);
      } else if (imported > 0 && skipped > 0) {
        toast.success(`Imported ${imported}, skipped ${skipped} — see details below`);
        setImportErrors(errors);
      } else {
        toast.error(`No candidates imported — ${skipped} row${skipped === 1 ? '' : 's'} skipped. See details below.`);
        setImportErrors(errors);
      }
      fetchCandidates();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); }
  };

  const openTagModal = (candidate) => {
    setTagCandidate(candidate);
    setSelectedTags([...(candidate.tags || [])]);
    setNotes(candidate.notes || '');
    setCustomTag('');
    setTagOpen(true);
  };

  const toggleTag = (tag) => setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);

  const addCustomTag = () => {
    const t = customTag.trim();
    if (t && !selectedTags.includes(t)) setSelectedTags([...selectedTags, t]);
    setCustomTag('');
  };

  const handleSaveTags = async () => {
    if (!tagCandidate) return;
    const key = tagCandidate.phone || tagCandidate.email;
    if (!key) { toast.error('No identifier'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/talent-vault/tags/${encodeURIComponent(key)}`, { tags: selectedTags, notes }, { headers: getHeaders() });
      toast.success('Tags & notes saved');
      setTagOpen(false);
      setCandidates(prev => prev.map(c => {
        if ((c.phone || c.email) === key) return { ...c, tags: selectedTags, notes, tagged_by: user.name, tagged_at: new Date().toISOString() };
        return c;
      }));
    } catch { toast.error('Failed to save tags'); }
    finally { setSaving(false); }
  };

  const handleAiSearch = async () => {
    if (!aiQuery.trim()) { toast.error('Enter a search query'); return; }
    setAiSearching(true);
    setAiResults(null);
    try {
      const res = await axios.post(`${API}/talent-vault/ai-search`, { query: aiQuery }, { headers: getHeaders() });
      setAiResults(res.data);
      if (res.data.results?.length === 0) toast.info('No matching candidates found. Try different criteria.');
      else toast.success(`Found ${res.data.results.length} matching candidates`);
    } catch (err) { toast.error(err.response?.data?.detail || 'AI search failed'); }
    finally { setAiSearching(false); }
  };

  const sourceBadge = (source) => {
    if (source?.includes('Resume') && source?.includes('CCR')) return <span className="bg-purple-100 text-purple-800 text-xs font-bold px-2 py-0.5 rounded">Both</span>;
    if (source?.includes('CCR')) return <span className="bg-amber-100 text-amber-800 text-xs font-bold px-2 py-0.5 rounded">CCR</span>;
    return <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-0.5 rounded">Resume</span>;
  };

  const clearFilters = () => {
    setSearchText(''); setFilterSource('all'); setFilterClient('all');
    setFilterStatus('all'); setFilterTag('all'); setFilterDateFrom(''); setFilterDateTo('');
  };

  // Group filtered candidates by pipeline stage for Kanban
  const kanbanData = useMemo(() => {
    const grouped = {};
    PIPELINE_STAGES.forEach(s => { grouped[s.key] = []; });
    filtered.forEach(c => {
      const stage = c.pipeline_stage || 'Sourced';
      if (grouped[stage]) grouped[stage].push(c);
      else grouped['Sourced'].push(c);
    });
    return grouped;
  }, [filtered]);

  // Kanban per-column pagination — render only the first N cards per column
  // to keep the DOM light. User can click "Show more" to load the next page.
  // Resets to page 1 when filters or view mode change.
  const KANBAN_PAGE_SIZE = 25;
  const [kanbanLimits, setKanbanLimits] = useState({});
  useEffect(() => { setKanbanLimits({}); }, [filterSignature]);
  const bumpKanban = (stage) => setKanbanLimits(l => ({
    ...l, [stage]: (l[stage] || KANBAN_PAGE_SIZE) + KANBAN_PAGE_SIZE
  }));

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-2" data-testid="talent-vault-title" style={{ fontFamily: 'Manrope, sans-serif' }}>TalentVault</h1>
          <p className="text-slate-600">Unified candidate database — Resumes + Calling Reports, deduplicated</p>
        </div>
        <div className="flex gap-2 items-center">
          {/* AI Search Button */}
          <Button data-testid="ai-search-btn" onClick={() => setAiSearchOpen(!aiSearchOpen)}
            className={`font-bold rounded ${aiSearchOpen ? 'bg-purple-700 hover:bg-purple-800' : 'bg-purple-600 hover:bg-purple-700'} text-white`}>
            <Sparkles size={16} className="mr-2" />AI Hiring Assistant
          </Button>
          {/* View Toggle */}
          <div className="flex bg-slate-100 rounded p-0.5" data-testid="view-toggle">
            <button data-testid="view-table-btn" onClick={() => setViewMode('table')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-bold transition-colors ${viewMode === 'table' ? 'bg-white text-blue-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              <TableProperties size={14} />Table
            </button>
            <button data-testid="view-kanban-btn" onClick={() => setViewMode('kanban')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-bold transition-colors ${viewMode === 'kanban' ? 'bg-white text-blue-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              <LayoutGrid size={14} />Kanban
            </button>
          </div>
          {canExport && (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="export-vault-btn" variant="outline" className="border-blue-200 text-blue-700 rounded font-bold">
                    <Download size={16} className="mr-2" />Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem data-testid="export-excel" onClick={() => handleExport('excel')}><FileSpreadsheet size={14} className="mr-2" />Excel</DropdownMenuItem>
                  <DropdownMenuItem data-testid="export-pdf" onClick={() => handleExport('pdf')}><FileText size={14} className="mr-2" />PDF</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button data-testid="import-vault-btn" onClick={() => setImportOpen(true)} variant="outline" className="border-blue-200 text-blue-700 rounded font-bold">
                <Upload size={16} className="mr-2" />Import
              </Button>
            </>
          )}
        </div>
      </div>

      {/* AI Search Panel */}
      {aiSearchOpen && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg shadow-sm p-5 mb-4" data-testid="ai-search-panel">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-purple-600 flex items-center justify-center flex-shrink-0">
              <Sparkles size={20} className="text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-purple-900 text-lg mb-1">AI Hiring Assistant</h3>
              <p className="text-xs text-purple-700 mb-3">Describe the ideal candidate in natural language — AI will search and rank matches from your TalentVault.</p>
              <div className="flex gap-2">
                <Input data-testid="ai-search-input" value={aiQuery} onChange={e => setAiQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !aiSearching) handleAiSearch(); }}
                  placeholder="e.g. Python developer with 5+ years experience, preferably from IT services companies, for Mumbai location..."
                  className="flex-1 border-purple-200 bg-white text-sm h-10" />
                <Button data-testid="ai-search-submit" onClick={handleAiSearch} disabled={aiSearching || !aiQuery.trim()}
                  className="bg-purple-600 hover:bg-purple-700 text-white font-bold h-10 px-6">
                  {aiSearching ? <><Loader2 size={14} className="mr-2 animate-spin" />Searching...</> : <><Search size={14} className="mr-2" />Find Matches</>}
                </Button>
              </div>
            </div>
          </div>

          {/* AI Results */}
          {aiResults && (
            <div className="mt-4 border-t border-purple-200 pt-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-bold text-purple-900">
                  {aiResults.results?.length > 0
                    ? `${aiResults.results.length} matches found from ${aiResults.total_searched} candidates`
                    : 'No matches found'}
                </p>
                {aiResults.results?.length > 0 && (
                  <Button size="sm" variant="ghost" onClick={() => setAiResults(null)} className="text-xs text-purple-600">Clear Results</Button>
                )}
              </div>
              {aiResults.error && <p className="text-sm text-red-600 mb-2">{aiResults.error}</p>}
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {(aiResults.results || []).map((r, idx) => {
                  const c = r.candidate;
                  const stageInfo = PIPELINE_STAGES.find(s => s.key === c.pipeline_stage) || PIPELINE_STAGES[0];
                  return (
                    <div key={`ai-${idx}`} data-testid={`ai-result-${idx}`}
                      className="bg-white rounded-lg border border-purple-100 p-4 flex items-center gap-4 hover:border-purple-300 transition-colors cursor-pointer"
                      onClick={() => openTagModal(c)}>
                      {/* Score Badge */}
                      <div className={`w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 font-extrabold text-lg ${r.score >= 80 ? 'bg-emerald-100 text-emerald-700 border-2 border-emerald-300' : r.score >= 60 ? 'bg-blue-100 text-blue-700 border-2 border-blue-300' : 'bg-amber-100 text-amber-700 border-2 border-amber-300'}`}>
                        {r.score}%
                      </div>
                      {/* Candidate Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-blue-900">{c.candidate_name}</span>
                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded text-white ${stageInfo.color}`}>{c.pipeline_stage}</span>
                        </div>
                        <p className="text-xs text-slate-600 truncate">{c.position || 'No position'} {c.client ? `@ ${c.client}` : ''}</p>
                        <p className="text-xs text-purple-700 mt-1 italic">{r.reason}</p>
                      </div>
                      {/* Contact */}
                      <div className="text-right flex-shrink-0 space-y-0.5">
                        {c.phone && <p className="text-xs text-slate-600 flex items-center gap-1 justify-end"><Phone size={10} />{c.phone}</p>}
                        {c.email && <p className="text-xs text-slate-500 flex items-center gap-1 justify-end"><Mail size={10} />{c.email}</p>}
                        <p className="text-[10px] text-slate-400">{c.recruiter_name || ''}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-blue-100 rounded shadow-sm p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Search</Label>
            <div className="relative mt-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="vault-search" value={searchText} onChange={e => setSearchText(e.target.value)} placeholder="Name, phone, email, position, client..." className="pl-9 h-9 rounded border-blue-200 text-sm" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Source</Label>
            <select data-testid="vault-filter-source" value={filterSource} onChange={e => setFilterSource(e.target.value)} className="mt-1 flex h-9 rounded border border-blue-200 bg-white px-3 py-1 text-sm">
              <option value="all">All Sources</option><option value="resume">Resume</option><option value="ccr">CCR</option><option value="both">Both</option>
            </select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Client</Label>
            <select data-testid="vault-filter-client" value={filterClient} onChange={e => setFilterClient(e.target.value)} className="mt-1 flex h-9 rounded border border-blue-200 bg-white px-3 py-1 text-sm max-w-[180px]">
              <option value="all">All Clients</option>{clients.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Status</Label>
            <select data-testid="vault-filter-status" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="mt-1 flex h-9 rounded border border-blue-200 bg-white px-3 py-1 text-sm max-w-[150px]">
              <option value="all">All Statuses</option>{statuses.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Tag</Label>
            <select data-testid="vault-filter-tag" value={filterTag} onChange={e => setFilterTag(e.target.value)} className="mt-1 flex h-9 rounded border border-blue-200 bg-white px-3 py-1 text-sm max-w-[150px]">
              <option value="all">All Tags</option>{allTags.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">From</Label>
            <Input data-testid="vault-filter-from" type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} className="mt-1 h-9 rounded border-blue-200 text-sm w-36" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">To</Label>
            <Input data-testid="vault-filter-to" type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} className="mt-1 h-9 rounded border-blue-200 text-sm w-36" />
          </div>
          <Button data-testid="vault-clear-filters" variant="ghost" onClick={clearFilters} className="text-slate-500 text-xs h-9">Clear</Button>
        </div>
        <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
          <span data-testid="vault-count">{filtered.length} candidates</span>
          <span>Total: {candidates.length}</span>
        </div>
      </div>

      {/* === TABLE VIEW === */}
      {viewMode === 'table' && (
        <div className="bg-white border border-blue-100 rounded shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-blue-50 border-b border-blue-100">
                <TableHead className="font-bold text-blue-900">Candidate</TableHead>
                <TableHead className="font-bold text-blue-900">Contact</TableHead>
                <TableHead className="font-bold text-blue-900">Position</TableHead>
                <TableHead className="font-bold text-blue-900">Client</TableHead>
                <TableHead className="font-bold text-blue-900">Source</TableHead>
                <TableHead className="font-bold text-blue-900">Stage</TableHead>
                <TableHead className="font-bold text-blue-900">Tags</TableHead>
                <TableHead className="font-bold text-blue-900">Recruiter</TableHead>
                <TableHead className="font-bold text-blue-900">Date</TableHead>
                <TableHead className="font-bold text-blue-900">CV</TableHead>
                <TableHead className="font-bold text-blue-900">Remarks</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={11} className="text-center py-8 text-slate-500">Loading...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={11} className="text-center py-8">
                  <Database size={48} className="mx-auto mb-2 text-slate-300" />
                  <p className="text-slate-500" data-testid="no-candidates-msg">No candidates found</p>
                </TableCell></TableRow>
              ) : pagedRows.map((c, idx) => {
                const stageInfo = PIPELINE_STAGES.find(s => s.key === c.pipeline_stage) || PIPELINE_STAGES[0];
                // idx is per-page; combine with effectivePage for a stable test id
                const rowIdx = (effectivePage - 1) * PAGE_SIZE + idx;
                return (
                  <TableRow key={`${c.phone || c.email}-${rowIdx}`} data-testid={`vault-row-${rowIdx}`} className="hover:bg-blue-50 cursor-pointer" onClick={() => openTagModal(c)}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-xs">{(c.candidate_name || '?')[0]}</div>
                        <span className="font-semibold text-blue-900 text-sm">{c.candidate_name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-0.5">
                        {c.phone && <div className="flex items-center gap-1 text-xs text-slate-600"><Phone size={10} />{c.phone}</div>}
                        {c.email && <div className="flex items-center gap-1 text-xs text-slate-500"><Mail size={10} />{c.email}</div>}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm max-w-[130px] truncate" title={c.position}>{c.position || '-'}</TableCell>
                    <TableCell className="text-sm">{c.client || '-'}</TableCell>
                    <TableCell>{sourceBadge(c.source)}</TableCell>
                    <TableCell>
                      <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded text-white ${stageInfo.color}`}>{c.pipeline_stage}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-[150px]">
                        {(c.tags || []).length > 0 ? (c.tags || []).map(t => (
                          <span key={t} className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${TAG_COLORS[t] || DEFAULT_TAG_COLOR}`}>{t}</span>
                        )) : <span className="text-xs text-slate-300">—</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">{c.recruiter_name || '-'}</TableCell>
                    <TableCell className="text-xs text-slate-500 whitespace-nowrap">{c.date || '-'}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {c.resume_file_path ? (
                        <Button
                          size="sm" variant="ghost"
                          data-testid={`vault-cv-${rowIdx}`}
                          title={`Uploaded: ${c.resume_uploaded_at ? new Date(c.resume_uploaded_at).toLocaleString() : '—'}\nFile: ${c.resume_filename || ''}`}
                          onClick={async () => {
                            try {
                              const res = await axios.get(`${API}/resumes/${c.resume_id}/cv`, {
                                headers: { Authorization: `Bearer ${token}` },
                                responseType: 'blob',
                              });
                              const url = window.URL.createObjectURL(new Blob([res.data]));
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = c.resume_filename || `cv-${c.candidate_name || c.resume_id}.pdf`;
                              document.body.appendChild(a); a.click(); a.remove();
                              window.URL.revokeObjectURL(url);
                            } catch (e) {
                              toast.error(e.response?.data?.detail || 'Download failed');
                            }
                          }}
                          className="text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50 px-2 h-7"
                        >
                          <FileText size={12} className="mr-1" />
                          <span className="text-[10px] font-semibold">CV</span>
                        </Button>
                      ) : (
                        <span className="text-xs text-slate-300">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600 max-w-[160px] truncate" title={c.remarks}>{c.remarks || '-'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {/* Pagination bar — only shown for the Table view (Kanban needs all rows
              to compute per-stage counts). Latest candidates are already on page 1
              because the backend sorts by date desc. */}
          {!loading && filtered.length > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-white text-sm">
              <div className="text-slate-600" data-testid="vault-page-summary">
                Showing <span className="font-semibold">{(effectivePage - 1) * PAGE_SIZE + 1}</span>
                {' '}–{' '}
                <span className="font-semibold">{Math.min(effectivePage * PAGE_SIZE, filtered.length)}</span>
                {' '}of{' '}
                <span className="font-semibold">{filtered.length}</span> candidates
                <span className="text-slate-400"> &middot; 50 per page &middot; latest first</span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 px-3"
                  onClick={() => setCurrentPage(1)}
                  disabled={effectivePage <= 1}
                  data-testid="vault-page-first"
                >First</Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 px-3"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={effectivePage <= 1}
                  data-testid="vault-page-prev"
                >Prev</Button>
                <span className="px-3 text-slate-700 font-medium" data-testid="vault-page-indicator">
                  Page {effectivePage} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 px-3"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={effectivePage >= totalPages}
                  data-testid="vault-page-next"
                >Next</Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 px-3"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={effectivePage >= totalPages}
                  data-testid="vault-page-last"
                >Last</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* === KANBAN VIEW === */}
      {viewMode === 'kanban' && (
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="kanban-board" style={{ minHeight: '60vh' }}>
          {PIPELINE_STAGES.map(stage => (
            <div key={stage.key} data-testid={`kanban-col-${stage.key.toLowerCase()}`} className="flex-shrink-0 w-[260px]">
              {/* Column Header */}
              <div className={`rounded-t p-3 flex items-center justify-between ${stage.color}`}>
                <span className="text-white text-xs font-bold uppercase tracking-wider">{stage.key}</span>
                <span className="bg-white/25 text-white text-xs font-bold px-2 py-0.5 rounded-full" data-testid={`kanban-count-${stage.key.toLowerCase()}`}>
                  {(kanbanData[stage.key] || []).length}
                </span>
              </div>
              {/* Column Body */}
              <div className={`border border-t-0 rounded-b min-h-[300px] p-2 space-y-2 ${stage.light}`}>
                {(kanbanData[stage.key] || []).length === 0 ? (
                  <p className="text-center text-xs text-slate-400 py-8">No candidates</p>
                ) : (
                  <>
                    {(kanbanData[stage.key] || []).slice(0, kanbanLimits[stage.key] || KANBAN_PAGE_SIZE).map((c, idx) => (
                    <div key={`${c.phone || c.email}-${idx}`}
                      data-testid={`kanban-card-${stage.key.toLowerCase()}-${idx}`}
                      onClick={() => openTagModal(c)}
                      className="bg-white rounded border border-slate-200 p-3 cursor-pointer hover:shadow-md hover:border-blue-300 transition-all">
                      {/* Name + Source */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className={`w-6 h-6 rounded-full ${stage.color} flex items-center justify-center text-white font-bold text-[10px] flex-shrink-0`}>
                            {(c.candidate_name || '?')[0]}
                          </div>
                          <span className="font-semibold text-blue-900 text-xs truncate">{c.candidate_name}</span>
                        </div>
                        {sourceBadge(c.source)}
                      </div>
                      {/* Position & Client */}
                      {c.position && <p className="text-xs text-slate-600 truncate" title={c.position}>{c.position}</p>}
                      {c.client && <p className="text-[10px] text-slate-400 truncate">{c.client}</p>}
                      {/* Tags */}
                      {(c.tags || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {c.tags.map(t => (
                            <span key={t} className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${TAG_COLORS[t] || DEFAULT_TAG_COLOR}`}>{t}</span>
                          ))}
                        </div>
                      )}
                      {/* Contact + Date */}
                      <div className="flex items-center justify-between mt-2 text-[10px] text-slate-400">
                        <span>{c.phone || c.email || ''}</span>
                        <span>{c.date || ''}</span>
                      </div>
                    </div>
                  ))}
                  {(kanbanData[stage.key] || []).length > (kanbanLimits[stage.key] || KANBAN_PAGE_SIZE) && (
                    <button
                      onClick={() => bumpKanban(stage.key)}
                      data-testid={`kanban-load-more-${stage.key.toLowerCase()}`}
                      className="w-full text-xs font-bold text-blue-700 hover:text-blue-900 bg-white border border-blue-200 rounded py-1.5 mt-1 hover:bg-blue-50 transition-colors"
                    >
                      Show more ({(kanbanData[stage.key] || []).length - (kanbanLimits[stage.key] || KANBAN_PAGE_SIZE)} more)
                    </button>
                  )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tag/Notes Modal */}
      <Dialog open={tagOpen} onOpenChange={setTagOpen}>
        <DialogContent className="rounded max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blue-900">
              <div className="flex items-center gap-2"><Tag size={20} />{tagCandidate?.candidate_name || 'Candidate'}</div>
            </DialogTitle>
            <DialogDescription>
              {tagCandidate?.phone && <span className="mr-3">{tagCandidate.phone}</span>}
              {tagCandidate?.email && <span>{tagCandidate.email}</span>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 mt-4">
            <div className="bg-slate-50 rounded p-3 grid grid-cols-2 gap-2 text-xs">
              <div><span className="font-bold text-slate-500">Position:</span> <span className="text-blue-900">{tagCandidate?.position || '-'}</span></div>
              <div><span className="font-bold text-slate-500">Client:</span> <span className="text-blue-900">{tagCandidate?.client || '-'}</span></div>
              <div><span className="font-bold text-slate-500">Source:</span> <span className="text-blue-900">{tagCandidate?.source || '-'}</span></div>
              <div><span className="font-bold text-slate-500">Stage:</span> <span className={`font-bold ${(PIPELINE_STAGES.find(s => s.key === tagCandidate?.pipeline_stage) || {}).text || 'text-slate-700'}`}>{tagCandidate?.pipeline_stage || '-'}</span></div>
              <div className="col-span-2"><span className="font-bold text-slate-500">Recruiter:</span> <span className="text-blue-900">{tagCandidate?.recruiter_name || '-'}</span> <span className="text-slate-400 ml-1">{tagCandidate?.date || ''}</span></div>
              {tagCandidate?.remarks && <div className="col-span-2"><span className="font-bold text-slate-500">Remarks:</span> <span className="text-slate-700">{tagCandidate.remarks}</span></div>}
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-blue-900 mb-2 block">Tags</Label>
              <div className="flex flex-wrap gap-2">
                {PREDEFINED_TAGS.map(tag => (
                  <button key={tag} data-testid={`tag-${tag.replace(/\s/g, '-').toLowerCase()}`} onClick={() => toggleTag(tag)}
                    className={`px-3 py-1.5 rounded border text-xs font-bold transition-all ${selectedTags.includes(tag) ? (TAG_COLORS[tag] || DEFAULT_TAG_COLOR) + ' ring-2 ring-offset-1 ring-blue-400' : 'bg-white text-slate-500 border-slate-200 hover:border-blue-300'}`}>
                    {selectedTags.includes(tag) && <span className="mr-1">&#10003;</span>}{tag}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {selectedTags.filter(t => !PREDEFINED_TAGS.includes(t)).map(t => (
                  <span key={t} className={`text-xs font-bold px-2 py-1 rounded border ${DEFAULT_TAG_COLOR} flex items-center gap-1`}>
                    {t}<button onClick={() => setSelectedTags(prev => prev.filter(x => x !== t))} className="hover:text-red-600"><X size={10} /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <Input data-testid="custom-tag-input" value={customTag} onChange={e => setCustomTag(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomTag(); } }} placeholder="Add custom tag..." className="h-8 rounded border-blue-200 text-sm flex-1" />
                <Button size="sm" variant="outline" data-testid="add-custom-tag-btn" onClick={addCustomTag} disabled={!customTag.trim()} className="h-8 text-xs border-blue-200">Add</Button>
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-blue-900 mb-2 block">Notes</Label>
              <textarea data-testid="tag-notes-input" value={notes} onChange={e => setNotes(e.target.value)} className="flex w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm min-h-[80px]" placeholder="Add notes about this candidate..." />
            </div>
            {tagCandidate?.tagged_by && <p className="text-xs text-slate-400">Last tagged by {tagCandidate.tagged_by} on {(tagCandidate.tagged_at || '').slice(0, 10)}</p>}
            <Button data-testid="save-tags-btn" onClick={handleSaveTags} disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">
              {saving ? 'Saving...' : 'Save Tags & Notes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="rounded max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blue-900">Bulk Import Candidates</DialogTitle>
            <DialogDescription>Import your existing candidate database from a CSV file into TalentVault.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleImport} className="space-y-4 mt-4">
            {/* Step 1: Download Template */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="font-bold text-blue-900 text-sm mb-2">Step 1: Download CSV Template</p>
              <p className="text-xs text-blue-700 mb-3">Download our template with the correct column headers. Fill it with your candidate data from your existing system.</p>
              <Button type="button" variant="outline" size="sm" className="border-blue-300 text-blue-700 hover:bg-blue-100"
                data-testid="download-template-btn"
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    const res = await axios.get(`${BACKEND_URL}/api/talent-vault/import-template`, {
                      headers: { Authorization: `Bearer ${token}` }, responseType: 'blob'
                    });
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const a = document.createElement('a'); a.href = url; a.download = 'talentvault_import_template.csv'; a.click();
                    window.URL.revokeObjectURL(url);
                    toast.success('Template downloaded');
                  } catch { toast.error('Download failed'); }
                }}>
                <Download className="h-4 w-4 mr-2" /> Download CSV Template
              </Button>
            </div>

            {/* CSV Format Info */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <p className="font-bold text-slate-800 text-sm mb-2">Required & Optional Columns</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <div>
                  <p className="font-bold text-emerald-700">Required:</p>
                  <ul className="text-slate-600 space-y-0.5 mt-1">
                    <li>Candidate Name</li>
                    <li>Phone <span className="text-slate-400">(or Email)</span></li>
                    <li>Email <span className="text-slate-400">(or Phone)</span></li>
                  </ul>
                </div>
                <div>
                  <p className="font-bold text-blue-700">Optional:</p>
                  <ul className="text-slate-600 space-y-0.5 mt-1">
                    <li>Position, Client, Status</li>
                    <li>Current Company, Experience</li>
                    <li>Location, Skills, CTC</li>
                    <li>Notice Period, Source</li>
                  </ul>
                </div>
              </div>
              <p className="mt-2 text-xs text-slate-500">Duplicates (matching phone or email) are automatically skipped.</p>
            </div>

            {/* Step 2: Upload */}
            <div>
              <p className="font-bold text-slate-800 text-sm mb-2">Step 2: Upload your CSV</p>
              <Input data-testid="import-file-input" type="file" accept=".csv" onChange={e => setImportFile(e.target.files[0])} className="rounded border-blue-200" />
            </div>
            <Button type="submit" data-testid="submit-import-btn" disabled={importing} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">
              {importing ? 'Importing...' : 'Import Candidates'}
            </Button>
            {importErrors.length > 0 && (
              <div data-testid="import-errors" className="bg-rose-50 border border-rose-200 rounded-lg p-4">
                <p className="font-bold text-rose-800 text-sm mb-2">⚠ {importErrors.length} row{importErrors.length === 1 ? '' : 's'} skipped — reasons:</p>
                <ul className="max-h-48 overflow-y-auto text-xs text-rose-700 space-y-1">
                  {importErrors.slice(0, 100).map((e, i) => (<li key={i}>• {e}</li>))}
                  {importErrors.length > 100 && <li className="italic">…and {importErrors.length - 100} more</li>}
                </ul>
                <p className="mt-2 text-xs text-rose-600">Fix the rows above in your CSV and re-upload. Already-imported rows won&apos;t be duplicated.</p>
              </div>
            )}
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
